import React, { PropsWithChildren, useState } from 'react'
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import axios from '../axios/axios';
import logo from "../assets/logo/boxlogo.svg";
import { ScrollArea, ScrollBar } from 'src/components/ui/scroll-area';
import { Doc, InfoSheet, MailPopup } from './PublicDocument';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { ArrowDownToLine, Check, Forward as ForwardIcon, Menu, X } from 'lucide-react';
import Preview from 'src/components/files/Preview';
import { downloadFile } from 'src/functions/actions/documents';
import Approve from 'src/components/workflow/Approve';
import Reject from 'src/components/workflow/Reject';
import Forward from 'src/components/workflow/Forward';
import Attachments from 'src/components/workflow/Attachments';
import { YYYYMMDDtoDate } from 'src/functions/format';
import { format } from 'date-fns';
import { getItemById } from 'src/functions/convert';
import Remarks from 'src/components/workflow/Remarks';
import Communication from 'src/components/workflow/Communication';
import { getWindevLang } from 'src/lib/lang';
import { useTranslation } from 'react-i18next';
import { Tab, TabItem } from 'src/components/ui/tab';
import DefaultView from 'src/components/public-document/views/default';
import TableView from 'src/components/public-document/views/table';
import { CONFIG } from 'src/config';
import empty from 'src/assets/images/empty.svg';
import { setCookie } from '../functions/general/cookies';

const PublicDoc = () => {
    const { t, i18n } = useTranslation()
    const [searchParams, setSearchParams] = useSearchParams()
    let encrypted_string = searchParams.get('es')

    const [docid_added_wf, setdocid_added_wf] = useState<number[]>([]);
    const [open_sidebar, setopen_sidebar] = useState(true);
    const [active_index, setactive_index] = useState(0);

    const [error_code, seterror_code] = useState<number | undefined>(undefined);

    const { data } = useQuery<Result | null>({
        queryKey: [`data_${encrypted_string}`],
        queryFn: async () => {
            try {
                const {data} = await axios.get(`documents/link?encrypted_string=${encrypted_string}`)
                if(data.errorcode!==0){ 
                    seterror_code(data.errorcode)
                    throw new Error(data.message)
                }
                let result = data.object
                if(Array.isArray(result.documents)){
                    result.documents = removeDuplicatesByDocid(result.documents);
                }
                setCookie('fit-token', result.token)
                return result
            } catch (error) {
                console.log(error);
                return null
            }
        },
        enabled: !!encrypted_string,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: true
    })

    let filteredDocuments = (data && Array.isArray(data?.documents)) ? [...data.documents] : []
    filteredDocuments = filteredDocuments.filter(d=>!docid_added_wf.includes(d.docid))

    const { data:metatypes } = useQuery<Metatype[] | undefined>({
        queryKey: [`metatypes`],
        queryFn: async () => {
            try {
                const {data:mt} = await axios.get(`metatypes/list`)
                if(mt.errorcode!==0){ throw new Error(mt.message) }
                return mt.object;
            } catch (error) {
                console.log(error)
                return undefined
            }
        },
        enabled: !!data,
        refetchOnWindowFocus: false
    })

    const { data:activeDocument } = useQuery<Doc | null>({
        queryKey: [`document_${filteredDocuments[active_index]?.docid}`],
        queryFn: async () => {
            try {
                const {data:doc} = await axios.get(`documents/get?docid=${filteredDocuments[active_index]?.docid}`)
                if(doc.errorcode!==0){ throw new Error(doc.message) }
                return doc.object;
            } catch (error) {
                console.log(error);
                return null
            }
        },
        enabled: !!data,
        refetchOnWindowFocus: false
    })

    const { data:doctypes } = useQuery<DoctypeObject[] | null>({
        queryKey: [`doctypes`],
        queryFn: async () => {
            try {
                const { data:result } = await axios.get(`doctypes/list`)
                if(result.errorcode!==0){throw new Error(result.message)}
                return result.object
            } catch (error) {
                return null
            }
        },
        enabled: !!data,
        refetchOnWindowFocus: false
    })

    const { data:workflowSettings } = useQuery<VerificationSetting[] | undefined>({
        queryKey: [`workflowSettings_${filteredDocuments[active_index]?.docid}`],
        queryFn: async () => {
            try {
                const actionId = filteredDocuments[active_index]?.wf?.wfdef_actionid
                if(!actionId){ return undefined } // No workflow actionid found
                const {data:wf} = await axios.get(`wf/verificationsettings?wfdef_actionid=${actionId}`)  
                if(wf.errorcode!==0){ throw new Error(wf.message) }   
                return wf.object.metadata;
            } catch (error) {
                console.log(error);
                return undefined
            }
        },
        enabled: !!(filteredDocuments[active_index]?.docid),
        refetchOnWindowFocus: false
    })

    const { data:communication, refetch:refetchCommunication } = useQuery<Communcation[] | null>({
        queryKey: [`communication_${filteredDocuments[active_index]?.docid}`],
        queryFn: async () => {
            try {
                const {data:com} = await axios.get(`communications/get?docid=${filteredDocuments[active_index]?.docid}`)
                if(com.errorcode!==0){throw new Error(com.message)}
                return com.object
            } catch (error) {
                console.log(error);
                return null
            }
        },
        enabled: !!data,
        refetchOnWindowFocus: false
    })

    const removeDuplicatesByDocid = (array:Document[]) => {
        return array.reduce((accumulator:Document[], current) => {
            if (!accumulator.some((item:Document) => item.docid === current.docid)) {
                accumulator.push(current);
            }
            return accumulator;
        }, []);
    };

    const handleDownloadFile = (document:Doc) => {
        downloadFile(document?.file, document.filename || 'FITdoc Document');
    }

    const getMetatypeOMS = (metatype:string) => {
        let value = '';
        if(!Array.isArray(metatypes)){ return metatype }
        const item = getItemById(metatypes, "metatype", metatype)
        if(item?.metatype_oms){
            item.metatype_oms.forEach(oms => {
                if(oms.nationid === getWindevLang(i18n.language)){
                    value = oms.oms
                }
            });
        }
        return value
    }

    const [active_approve, setactive_approve] = useState<Document | undefined>(undefined);
    const [active_reject, setactive_reject] = useState<Document | undefined>(undefined);
    const [active_forward, setactive_forward] = useState<Document | undefined>(undefined);

    function handleOnApprove() {
        //refetchData()
        setdocid_added_wf(p=>{ 
            const selectedDocid = filteredDocuments[active_index]?.docid
            if(selectedDocid){
                return [...p, selectedDocid]
            }
            return [...p]
        })
    }

    function handleOnReject() {
        //refetchData()
        setdocid_added_wf(p=>{ 
            const selectedDocid = filteredDocuments[active_index]?.docid
            if(selectedDocid){
                return [...p, selectedDocid]
            }
            return [...p]
        })
    }

    function handleOnApproveRejectBulk(docids:number[]) {
        setdocid_added_wf([...docid_added_wf.concat(docids)])
    }

    function handleOnForward() {
        //refetchData()
        setdocid_added_wf(p=>{ 
            const selectedDocid = filteredDocuments[active_index]?.docid
            if(selectedDocid){
                return [...p, selectedDocid]
            }
            return [...p]
        })
    }

    const [selected_tab, setselected_tab] = useState('default');

  return (
    <>
    <Approve open={!!active_approve} settings={workflowSettings} onSuccess={()=>{ handleOnApprove() }} onOpenChange={(o)=>{if(o){} else{setactive_approve(undefined)}}} metatypes={metatypes} document={active_approve} />
    <Reject open={!!active_reject} settings={workflowSettings} onSuccess={()=>{ handleOnReject() }} onOpenChange={(o)=>{if(o){} else{setactive_reject(undefined)}}} metatypes={metatypes} document={active_reject} />
    {filteredDocuments[active_index]?.docid && <Forward onSuccess={()=>{ handleOnForward() }} open={!!active_forward} docid={filteredDocuments[active_index]?.docid} onOpenChange={(o)=>{if(o){} else{setactive_forward(undefined)}}} />}

    <main className='h-[100dvh] flex flex-col'>

        {/* Header */}
        <div className='h-20 min-h-[5rem] flex justify-center items-center border-b'>
            <div className='container grid grid-cols-3'>
                <img src={logo} className='w-auto h-10' alt="Fitdoc logo"/>
                <div className='flex items-center justify-center'>
                    {CONFIG.workflow.viewTableLayout && 
                    <Tab value={selected_tab} onValueChange={(v)=>{setselected_tab(v)}}>
                        <TabItem value='default'>Standaard</TabItem>
                        <TabItem value='table'>Tabel</TabItem>
                    </Tab>
                    }
                </div>
                <div className='flex items-center justify-end gap-2'>
                    <div className='flex items-center justify-center font-semibold rounded-full w-9 h-9 bg-primary text-primary-foreground'>{data?.user?.name.charAt(0)}</div>
                    <p className='font-semibold'>{data?.user?.name} {data?.user?.lastname}</p>
                </div>
            </div>
        </div>

        {error_code &&
        <div className='flex flex-col items-center justify-center gap-8 py-8 sm:py-16'>
            <img src={empty} alt="Fout" className='w-32 sm:w-52'/>

            <div className='flex flex-col items-center justify-center text-center'>
                <p className='text-lg font-semibold'>Helaas! Er is een fout opgetreden</p>
                {error_code === 404 && <p className='text-sm'>[{error_code}] De gevraagde documenten zijn niet gevonden</p>}
                {error_code === 701 && <p className='text-sm'>[{error_code}] Kan de sleutel niet decoderen</p>}
                {error_code === 706 && <p className='text-sm'>[{error_code}] Portaalsleutel verlopen</p>}
                {error_code === 801 && <p className='text-sm'>[{error_code}] Geen gecodeerde tekenreeks gevonden</p>}
                {(error_code !== 404 && error_code !== 701 && error_code !== 706 && error_code !== 801) && <p className='text-sm'>[{error_code}] Onbekende fout</p>}
            </div>
        </div>
        }

        {!error_code && <>

            {selected_tab === 'default' && <DefaultView activeDocument={activeDocument} docid_added_wf={docid_added_wf} data={data} filteredDocuments={filteredDocuments} active_index={active_index} communication={communication} refetchCommunication={refetchCommunication} handleDownloadFile={handleDownloadFile} setactive_index={setactive_index} doctypes={doctypes} metatypes={metatypes} getMetatypeOMS={getMetatypeOMS} setactive_approve={setactive_approve} setactive_forward={setactive_forward} setactive_reject={setactive_reject}/>}

            {selected_tab === 'table' && <TableView onSuccess={()=>{}} activeDocument={activeDocument} docid_added_wf={docid_added_wf} handleOnForward={handleOnApproveRejectBulk} handleOnApprove={handleOnApproveRejectBulk} handleOnReject={handleOnApproveRejectBulk} data={data} filteredDocuments={filteredDocuments} active_index={active_index} communication={communication} refetchCommunication={refetchCommunication} handleDownloadFile={handleDownloadFile} setactive_index={setactive_index} doctypes={doctypes} metatypes={metatypes} getMetatypeOMS={getMetatypeOMS} setactive_approve={setactive_approve} setactive_forward={setactive_forward} setactive_reject={setactive_reject}/>}
        
        </>}
    </main>
    </>
  )
}

export default PublicDoc

export interface VerificationSetting {
    metatype: string;
    multiple: boolean;
    required: boolean
}

export interface Result {
    token:     string;
    user:      ResultUser;
    documents?: Document[];
}

export interface ResultUser {
    companyname: string;
    name:        string;
    lastname:    string;
}

export interface Document {
    docid:    number;
    docdate:  string;
    version:  number;
    doctype:  string;
    user:     string;
    filename: string;
    metadata: Metadata[];
    remarks:  any[];
    options:  Options;
    wf:       Wf;
    attachments?: Attachment[]
}

export interface Metadata {
    metatype:  string;
    metavalue: string;
    required?: boolean;
}

export interface Options {
    wf:       number;
    favorite: number;
}

export interface Wf {
    users: UserElement[];
    wfdef_actionid?: number;
    actiontype?: string
}

export interface UserElement {
    username: string;
    remark:   string;
    result:   string;
}

export interface Metatype {
    metatype:          string;
    required:          number;
    metatype_oms:      MetatypeOm[];
    showincompactview: number;
    datatype:          string;
    displaytype:       string;
    columndisplay:     boolean;
}

export interface MetatypeOm {
    nationid: number;
    oms:      string;
}